// Research Deck API — drops a job message into the research-deck pipeline
// via the same AppSync endpoint as the Ad Agency / Reporter, using jobType
// "TRIGGER_RESEARCH_DECK_JOB". The AppSync resolver routes this to
// research-deck-queue → TriggerResearchDeckTask Lambda → ecs.RunTask Fargate.
import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import {
  TRIGGER_AD_AGENCY_JOB_MUTATION,
  GET_LIBRARY_CARD_QUERY,
} from "../queries/libraryCardQueries";
import { getAuthToken } from "../utils/authUtils";

const GRAPHQL_ENDPOINT =
  "https://m2jxbyhagffjthxtsiuwb2iiwe.appsync-api.us-east-1.amazonaws.com/graphql";

const httpLink = createHttpLink({ uri: GRAPHQL_ENDPOINT });

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: token } : {}),
    },
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

// Expected payload shape:
//   { runId, tenantId, topic, voiceMode, outputFormat?, voiceId?, slideCount?,
//     iv?, targetSeconds?,
//     inputType?: "research" | "video-source" | "doc-source",  // default "research"
//     sourceTicketId?: string,   // required when inputType !== "research"
//     sourceTenantId?: string,   // optional, defaults to tenantId
//     diagram?: { repos, description, direction, lineStyle, detailLevel } }
// inputType=video-source / doc-source skip the X20-library research step and
// substitute an adapter that lifts the bundle out of an existing
// PROCESSEDVIDEOS row — used by Assets Studio's "Generate PowerPoint" button.
export const triggerResearchDeckApi = async (payload) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_RESEARCH_DECK_JOB",
        tenantId: payload.tenantId,
        payload: JSON.stringify(payload),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// Convenience wrapper for "generate a PPTX from an existing video/doc ticket".
// Used by Assets Studio's "Generate PowerPoint" button — fans out the same
// research-deck pipeline but with the analyzer-source adapter swapped in for
// research-library, and PPTX-only output (no narration / avatar / synthesis).
//
// The Fargate task patches the source PROCESSEDVIDEOS row in place with the
// pptx artifact, so the new asset surfaces on the same Assets Studio card.
export const triggerDeckFromSourceApi = async ({
  runId,
  tenantId,
  sourceTicketId,
  sourceKind,           // "video-source" | "doc-source"
  topic,                // optional, falls back to analyzer-derived title
  slideCount = 15,
}) => {
  return triggerResearchDeckApi({
    runId,
    tenantId,
    topic: topic || "Research deck",
    voiceMode: "slides-only",
    outputFormat: "pptx",
    inputType: sourceKind,
    sourceTicketId,
    slideCount,
  });
};

// Confirm the diagrams-agent's proposed scope for a deck run that's paused at
// phase=awaiting_diagram_confirm. Patches RESEARCHDECKTICKET so the Fargate
// task's wait-loop picks up the confirmation on its next poll and kicks off
// the diagrams render.
export const triggerDeckConfirmDiagramScopeApi = async ({
  runId,
  tenantId,
  confirmedScope,
  repoRefs,
}) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_DECK_CONFIRM_DIAGRAM_SCOPE",
        tenantId,
        payload: JSON.stringify({ runId, confirmedScope, repoRefs }),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// Ask the deck to re-interpret the diagram with a new description. Capped at
// 3 refines per deckRunId by the FE; the BE additionally hard-caps in the
// resolver so a stale tab can't blow through the limit.
export const triggerDeckRefineDiagramApi = async ({
  runId,
  tenantId,
  newDescription,
}) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_DECK_REFINE_DIAGRAM",
        tenantId,
        payload: JSON.stringify({ runId, newDescription }),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// Fetch a RESEARCHDECKTICKET row via the shared library-card resolver.
// The BE writes all ticket fields inside a JSON-stringified `data` field
// (library-card convention), so we parse it before handing back to the UI.
// Returns null if the row hasn't been written yet (happens briefly after
// submit, before TriggerResearchDeckTask Lambda drains the queue).
export const getResearchDeckTicketApi = async ({ tenantId, runId }) => {
  try {
    const { data } = await client.query({
      query: GET_LIBRARY_CARD_QUERY,
      variables: {
        input: {
          operationType: "GET_LIBRARY_CARD",
          tenantId,
          entityType: "RESEARCHDECKTICKET",
          entityId: runId,
        },
      },
      fetchPolicy: "no-cache",
    });
    const card = data?.getLibraryCard;
    if (!card || !card.data) return null;
    // The resolver returns `data` as an already-parsed object for some
    // entity types and a JSON string for others — handle both.
    return typeof card.data === "string" ? JSON.parse(card.data) : card.data;
  } catch (err) {
    // Not-found is the normal pre-QUEUED case; treat as "still pending".
    const msg = String(err?.message || "");
    if (msg.includes("not found")) return null;
    throw err;
  }
};
